import React, { useEffect, useState } from 'react';
import { Image, ActivityIndicator } from 'react-native';
import Colors from '../constants/colors';
import ProfileWizard from './Components/ProfileWizard';
import { ProfileApi } from './api';
import type { SettingProps } from './api/types';
import type { CodeRequestProps, FocusPositionProps, MyPlayerProps, PlayerBalanceProps, PlayerReferralProps, PlayerSettingProps, PromoProps, PublicPlayerProps, RewardOptionProps } from '../types';
import { Text, View } from '../Components/Themed';

type ProfileOverviewProps = {
    show_welcome?:boolean,
    hide_powered_by?:boolean,
    float?:boolean,
    hide_profile_header?:boolean,
    player?:MyPlayerProps,
    style?:any,
    keyboard_height:number,
    hidden_groups?:string[],
    insets?:{ top:number, bottom:number, left:number, right:number }
    hidden_steps?:string[],
    hidden_levels?:string[],
    onClose:() => void,
    view_mode?:'desktop'|'mobile',
    onFocusPosition?:(data:FocusPositionProps) => void,
    onVouchStart?:() => void,
    onLogout:() => void,
    onRedeemCode:(code_details:{
        code_request?:CodeRequestProps,
        promo?:PromoProps,
        player_referral?:PlayerReferralProps,
        reward_option?:RewardOptionProps,
        referrer?:PublicPlayerProps
    }) => void,
    onNextStep?:(step:string) => void,
    onPlayerUpdate?:(p:MyPlayerProps, attribute?:string) => void,
    profile_attribute?:'basic'|'phone'|'email'|'dob'|'password'|'vouch'|'notification'|'preferences',
    walkthrough?: {
        level: 'verify' | 'basic'
    }
}

const ProfileManager = ({ float, profile_attribute, style, keyboard_height, view_mode, hide_profile_header, insets, player, hide_powered_by, onRedeemCode, onPlayerUpdate, onNextStep, onFocusPosition, onVouchStart, show_welcome, hidden_groups, hidden_steps, hidden_levels, walkthrough, onClose, onLogout }: ProfileOverviewProps ) => {
    const [ width, setModuleWidth ] = useState(400);
    const [ loading, setLoading ] = useState(false);
    const [ vouching, setVouching ] = useState(false);
    const [ draft_player, setDraftPlayer ] = useState<MyPlayerProps>();
    const [ player_balance, setPlayerBalance ] = useState<PlayerBalanceProps>();
    const [ settings, setSettings ] = useState<SettingProps[]>([]);
    const [ player_settings, setPlayerSettings ] = useState<PlayerSettingProps[]>([]);

    useEffect(() => {
        getPlayerFromServer()
    },[])

    useEffect(() => {
        if(!player){ return }
        if(!draft_player){ return } //Dont load over the initial api call
        setDraftPlayer(player); //If we get an update from the player - then update it in our UX
    },[player])

    const getPlayerFromServer = async() => {
        setLoading(true);
        ProfileApi.setEnvironment();
        try {
            const resp = await ProfileApi.getMyDetails();
            const pb = await ProfileApi.getMyBalance();
            const settings_response = await ProfileApi.getMySettings();
            setSettings(settings_response.settings);
            setPlayerSettings(settings_response.player_settings);
            setPlayerBalance(pb);
            setDraftPlayer(resp.player);
        } catch (e) {
            console.log(e)
        }
        setLoading(false);
    }

    const handleLogout = () => {
        setDraftPlayer(undefined);
        setPlayerBalance(undefined);
        onLogout()
        onClose()
    }

    if(!draft_player || loading){
        return (
            <View style={{ flex:1 }}>
                <ActivityIndicator size={'large'} color={Colors.brand.midnight} />
            </View>
        )
    }

    return (
        <View float={float} style={{ flex:1 }}>
            <View style={{ flex:1, ...style }} onLayout={(ev) => {
            const { width } = ev.nativeEvent.layout
            setModuleWidth(width)
        }}>
                {!hide_profile_header && !walkthrough && !vouching ?
                <View type='header' style={{ flexDirection:'row', alignItems:'center', borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                    <View transparent nativeID='profile_picture' style={{ padding:10 }}>
                        <Image
                            source={{ uri: draft_player.profile_pic }}
                            style={{ height:50, width:50, borderRadius:100 }}
                            resizeMode='cover'
                        />
                    </View>
                    <View transparent style={{ flex:1 }}>
                        <Text size={20} theme='h1'>{draft_player.first_name} {draft_player.last_name}</Text>
                        <Text style={{ marginTop:5 }} size={16} theme='h2'>@{draft_player.username}</Text>
                    </View>
                    {player_balance ?
                    <View transparent style={{ paddingRight:10 }}>
                        <Text size={16} theme='h1' textAlign='right'>${player_balance.balance.toFixed(2)}</Text>
                        <Text style={{ marginTop:3 }} theme='description' textAlign='right'>Promo {player_balance.promo_balance.toFixed(2)}</Text>
                    </View>
                    :<></>}
                </View>
                :<></>}
                <View style={{ flex:1, paddingBottom:keyboard_height }}>
                    <ProfileWizard
                        player={draft_player}
                        player_settings={player_settings}
                        onVouching={(active) => {
                            if(active && onVouchStart){ onVouchStart() }
                            setVouching(active)
                        }}
                        width={width}
                        onRedeemCode={onRedeemCode}
                        settings={settings}
                        insets={insets}
                        view_mode={view_mode}
                        show_welcome={show_welcome}
                        onFocusPosition={onFocusPosition}
                        hide_powered_by={hide_powered_by}
                        hidden_groups={hidden_groups}
                        hidden_steps={hidden_steps}
                        hidden_levels={hidden_levels}
                        init_walkthrough={walkthrough}
                        onNextStep={(step:string) => onNextStep ? onNextStep(step) : console.log()}
                        onClose={() => onClose()}
                        onLogout={() => handleLogout()}
                        profile_attribute={profile_attribute}
                        onSelectLevel={(level) => console.log(level)}
                        onUpdatePlayerSetting={(new_ps) => setPlayerSettings(player_settings.filter(ps => ps.player_setting_id != new_ps.player_setting_id).concat(new_ps))}
                        onPlayerUpdate={(player, attribute) => {
                            setDraftPlayer(player)
                            if(onPlayerUpdate){ onPlayerUpdate(player, attribute) }
                        }}
                    />
                </View>
            </View>
        </View>
    )
}

export default ProfileManager